/**
 * Waypoint Mutation Utilities
 *
 * Shared functions for updating and creating waypoints in a mission.
 * These replace manual protobuf manipulation that was previously
 * duplicated across cloud and mobile codebases.
 *
 * @packageDocumentation
 */

import { cloneDeep, set } from "lodash";

import { StartVideoActionArgs } from "@skydio/pbtypes/pbtypes/vehicle/skills/actions/start_video/start_video";
import { StopVideoActionArgs } from "@skydio/pbtypes/pbtypes/vehicle/skills/actions/stop_video/stop_video";
import { TimedWaitActionArgs } from "@skydio/pbtypes/pbtypes/vehicle/skills/actions/timed_wait/timed_wait";
import { FrameType_Enum } from "@skydio/pbtypes/pbtypes/vehicle/skills/waypoints";

import {
  createAction,
  getRootSequence,
  getWaypointActions,
  updateAction,
} from "../actionTree/ActionTreeUtils";
import { createDefaultWaypoint, initActionType, isWaypoint } from "../actionTree/TypeGuards";
import { getWaypoints } from "./getters";

import type { Mission } from "@skydio/pbtypes/pbtypes/vehicle/skills/mission";
import type { HeadingMode_Enum } from "@skydio/pbtypes/pbtypes/vehicle/skills/waypoints";
import type { NodeIndexMap, VehicleConfiguration, Waypoint } from "../actionTree/types";
import type { InTransitCameraActionType } from "../waypoint-utils";

/**
 * Fields that can be updated on an existing waypoint.
 * All angle fields are in degrees (converted to radians internally).
 */
export interface WaypointFieldUpdates {
  /** GPS latitude (x coordinate) */
  latitude?: number;
  /** GPS longitude (y coordinate) */
  longitude?: number;
  /** Height in meters */
  height?: number;
  /** Heading in degrees (stored as radians) */
  headingDeg?: number;
  /** Gimbal pitch in degrees (stored as radians) */
  gimbalPitchDeg?: number;
  /** Transit speed in m/s */
  speed?: number;
  /** Pause time at waypoint in seconds */
  pauseTime?: number;
  /** Heading mode for in-transit behavior */
  headingMode?: HeadingMode_Enum;
  /** Camera action during transit to waypoint */
  inTransitCameraAction?: InTransitCameraActionType;
}

/**
 * Configuration for creating a new waypoint.
 * Latitude and longitude are required; other fields have sensible defaults.
 */
export interface WaypointCreationConfig {
  /** GPS latitude (required) */
  latitude: number;
  /** GPS longitude (required) */
  longitude: number;
  /** Height in meters (default: 50) */
  height?: number;
  /** Heading in degrees (default: 0) */
  headingDeg?: number;
  /** Gimbal pitch in degrees (default: -45) */
  gimbalPitchDeg?: number;
  /** Transit speed in m/s (default: 5) */
  speed?: number;
  /** Pause time in seconds (default: 0) */
  pauseTime?: number;
}

/**
 * Update one or more fields on an existing waypoint in a mission.
 *
 * Uses cloneDeep + lodash set() to safely mutate a deep copy of the waypoint,
 * then updateAction() to swap it into the mission immutably.
 *
 * @param mission - The mission containing the waypoint
 * @param nodeIndexMap - The node index map for O(1) path lookups
 * @param waypointIndex - 0-based index of the waypoint to update
 * @param updates - Fields to update
 * @returns Updated mission and node index map
 * @throws Error if waypointIndex is out of range or the action is not a valid waypoint
 */
export function updateWaypointAtIndex(
  mission: Mission,
  nodeIndexMap: NodeIndexMap,
  waypointIndex: number,
  updates: WaypointFieldUpdates
): { missionSpec: Mission; _nodeIndexMap: NodeIndexMap } {
  const waypoints = getWaypoints(mission);

  if (waypointIndex < 0 || waypointIndex >= waypoints.length) {
    throw new Error(`Waypoint index ${waypointIndex} out of range (0-${waypoints.length - 1})`);
  }

  const original = waypoints[waypointIndex];
  if (!isWaypoint(original)) {
    throw new Error(`Action at index ${waypointIndex} is not a valid waypoint`);
  }

  const clone: Waypoint = cloneDeep(original);
  const { position, timedWait } = getWaypointActions(clone);

  if (updates.latitude !== undefined) {
    set(position, "args.gotoWaypoint.waypoint.xy.x", updates.latitude);
  }
  if (updates.longitude !== undefined) {
    set(position, "args.gotoWaypoint.waypoint.xy.y", updates.longitude);
  }
  if (updates.height !== undefined) {
    set(position, "args.gotoWaypoint.waypoint.z.value", updates.height);
  }
  if (updates.headingDeg !== undefined) {
    set(position, "args.gotoWaypoint.waypoint.heading.value", updates.headingDeg * (Math.PI / 180));
  }
  if (updates.gimbalPitchDeg !== undefined) {
    set(
      position,
      "args.gotoWaypoint.waypoint.gimbalPitch.value",
      updates.gimbalPitchDeg * (Math.PI / 180)
    );
  }
  if (updates.speed !== undefined) {
    set(position, "args.gotoWaypoint.motionArgs.traversalArgs.speed", updates.speed);
  }
  if (updates.pauseTime !== undefined && timedWait) {
    set(timedWait, "args.timedWait.timeout.value", updates.pauseTime);
  }

  // Update heading mode in lookAtArgs
  if (updates.headingMode !== undefined) {
    // Ensure lookAtArgs exists
    if (!position.args.gotoWaypoint?.motionArgs?.lookAtArgs) {
      set(position, "args.gotoWaypoint.motionArgs.lookAtArgs", {});
    }
    set(position, "args.gotoWaypoint.motionArgs.lookAtArgs.headingMode", updates.headingMode);
  }

  // Handle in-transit camera action by modifying the waypoint's action sequence
  if (updates.inTransitCameraAction !== undefined) {
    const sequenceActions = clone.args.sequence?.actions;
    if (sequenceActions) {
      // Find existing video action index (startVideo or stopVideo)
      const existingVideoIdx = sequenceActions.findIndex(
        a => a.actionKey === "StartVideo" || a.actionKey === "StopVideo"
      );

      if (updates.inTransitCameraAction === "startVideo") {
        // Create a startVideo action using initActionType for proper typing
        const startVideoAction = initActionType(
          "startVideo",
          StartVideoActionArgs.fromPartial({
            cameraSettings: {},
          })
        );
        // Preserve existing UUID if replacing
        if (existingVideoIdx >= 0) {
          startVideoAction.actionUuid = sequenceActions[existingVideoIdx].actionUuid;
        }

        if (existingVideoIdx >= 0) {
          // Replace existing video action
          sequenceActions[existingVideoIdx] = startVideoAction;
        } else {
          // Insert before GotoWaypoint (typically at index 0 or 1)
          const gotoIdx = sequenceActions.findIndex(a => a.actionKey === "GotoWaypoint");
          const insertIdx = gotoIdx > 0 ? gotoIdx : 0;
          sequenceActions.splice(insertIdx, 0, startVideoAction);
        }
      } else if (updates.inTransitCameraAction === "none") {
        // Convert to stopVideo or remove video action
        if (existingVideoIdx >= 0) {
          const stopVideoAction = initActionType("stopVideo", StopVideoActionArgs.fromPartial({}));
          stopVideoAction.actionUuid = sequenceActions[existingVideoIdx].actionUuid;
          sequenceActions[existingVideoIdx] = stopVideoAction;
        }
      }
      // Note: intervalPhotos not yet implemented
    }
  }

  return updateAction(mission, nodeIndexMap, clone);
}

/**
 * Create a new waypoint and add it to the end of the mission's root sequence.
 *
 * Creates a default GPS waypoint via createDefaultWaypoint(), applies the
 * provided config fields, then inserts it into the root sequence.
 *
 * @param mission - The mission to add the waypoint to
 * @param nodeIndexMap - The node index map
 * @param config - Waypoint position and optional settings
 * @param vehicleConfig - Optional vehicle configuration (affects defaults)
 * @returns Updated mission and node index map
 */
export function addWaypointToMission(
  mission: Mission,
  nodeIndexMap: NodeIndexMap,
  config: WaypointCreationConfig,
  vehicleConfig?: VehicleConfiguration
): { missionSpec: Mission; _nodeIndexMap: NodeIndexMap } {
  const pauseTime = config.pauseTime ?? 0;

  // Create TimedWait action for pause time
  const timedWaitAction = initActionType(
    "timedWait",
    TimedWaitActionArgs.fromPartial({
      timeout: { value: pauseTime },
    })
  );

  // Create default waypoint with GPS frame
  const newWaypoint = createDefaultWaypoint({
    xyFrame: FrameType_Enum.GPS,
    timedWaitInit: timedWaitAction,
    vehicleConfiguration: vehicleConfig,
  });

  // Apply config fields via lodash set()
  const { position, timedWait } = getWaypointActions(newWaypoint);

  set(position, "args.gotoWaypoint.waypoint.xy.x", config.latitude);
  set(position, "args.gotoWaypoint.waypoint.xy.y", config.longitude);
  set(position, "args.gotoWaypoint.waypoint.xy.frame", FrameType_Enum.GPS);

  set(position, "args.gotoWaypoint.waypoint.z.value", config.height ?? 50);

  set(
    position,
    "args.gotoWaypoint.waypoint.heading.value",
    (config.headingDeg ?? 0) * (Math.PI / 180)
  );

  set(
    position,
    "args.gotoWaypoint.waypoint.gimbalPitch.value",
    (config.gimbalPitchDeg ?? -45) * (Math.PI / 180)
  );

  if (position.args.gotoWaypoint?.motionArgs?.traversalArgs) {
    set(position, "args.gotoWaypoint.motionArgs.traversalArgs.speed", config.speed ?? 5);
  }

  if (timedWait) {
    set(timedWait, "args.timedWait.timeout.value", pauseTime);
  }

  // Insert into root sequence at the end
  const { rootSequenceUuid, actions } = getRootSequence(mission);
  const insertIndex = actions.length;

  return createAction(
    mission,
    nodeIndexMap,
    newWaypoint,
    rootSequenceUuid,
    insertIndex,
    "generate"
  );
}
